import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  fetchBirthAndWorkAnniversary,
  fetchUpcomingConfirmations,
} from "../../store/slices/hrDashboardSlice";
import { Cake, Briefcase, Calendar, Bell } from "lucide-react";
import AnniversaryDetailModal from "./AnniversaryDetailModal";
import ConfirmationDetailModal from "./ConfirmationDetailModal";

const STYLES = {
  confirmation: {
    bg: "bg-blue-50 border-blue-200", // Soft Blue
    text: "text-blue-700",
    border: "border-l-4 border-blue-500",
  },
  anniversary: {
    bg: "bg-fuchsia-50 border-fuchsia-200", // Soft Pink/Fuchsia
    text: "text-fuchsia-700",
    border: "border-l-4 border-fuchsia-500",
  },
  leave: {
    bg: "bg-amber-50 border-amber-200", // Soft Amber/Yellow
    text: "text-amber-700",
    border: "border-l-4 border-amber-500",
  },
};

interface StackCardProps {
  icon: React.ElementType;
  title: string;
  count: number;
  style: (typeof STYLES)["confirmation" | "anniversary" | "leave"];
  onClick: () => void;
}

const StackCard: React.FC<StackCardProps> = ({
  icon: Icon,
  title,
  count,
  style,
  onClick,
}) => {
  // Only render the card if count is greater than 0
  if (count === 0) return null;

  return (
    <div
      className={`rounded-xl shadow-md p-5 cursor-pointer transform hover:shadow-lg transition duration-200 ${style.bg} ${style.border}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="text-5xl font-extrabold text-gray-800">{count}</div>
        <Icon className={`h-8 w-8 ${style.text} opacity-80`} />
      </div>
      <p className="mt-2 text-sm font-medium text-gray-600">{title}</p>
      <p className="text-xs text-gray-500 mt-1 italic">Click to view details</p>
    </div>
  );
};

const HrDashboardStack: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const {
    anniversaries = [],
    confirmations = [],
    loading,
  } = useSelector((state: RootState) => state.hrDashboard);

  // State to manage which modal is open and its data
  const [isAnniversaryModalOpen, setIsAnniversaryModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  useEffect(() => {
    const now = new Date();
    dispatch(
      fetchBirthAndWorkAnniversary({
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      })
    );
    dispatch(fetchUpcomingConfirmations());
  }, [dispatch]);

  // Split and count the anniversary data
  const { birthdayCount, anniversaryCount, birthdayList, anniversaryList } =
    useMemo(() => {
      const birthdayList = anniversaries.filter((item) => item.birthday);
      const anniversaryList = anniversaries.filter(
        (item) => item.workAnniversary
      );
      return {
        birthdayCount: birthdayList.length,
        anniversaryCount: anniversaryList.length,
        birthdayList,
        anniversaryList,
      };
    }, [anniversaries]);

  const totalAnniversaryCount = birthdayCount + anniversaryCount;
  const confirmationCount = confirmations.length;
  // Placeholder for future stack, set to 0 to conditionally hide it
  const pendingLeaveApprovalCount = 0;

  const handleStackClick = (type: "anniversary" | "confirmation") => {
    if (type === "anniversary" && totalAnniversaryCount > 0) {
      setIsAnniversaryModalOpen(true);
    } else if (type === "confirmation" && confirmationCount > 0) {
      setIsConfirmationModalOpen(true);
    }
  };

  if (loading)
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        <Bell className="h-4 w-4 mr-1 inline animate-pulse text-blue-500" />
        Loading HR updates...
      </div>
    );

  const hasAnyData =
    confirmationCount > 0 ||
    totalAnniversaryCount > 0 ||
    pendingLeaveApprovalCount > 0;

  if (!hasAnyData) {
    return (
      <div className="p-8 text-center bg-white rounded-xl shadow-lg border border-gray-100">
        <Bell className="h-6 w-6 mx-auto mb-3 text-gray-400" />
        <p className="text-lg font-semibold text-gray-700">
          No Upcoming Events
        </p>
        <p className="text-sm text-gray-500 mt-1">
          Check back later for pending confirmations, birthdays, and
          anniversaries.
        </p>
      </div>
    );
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b border-gray-200 pb-2">
        <Bell className="h-5 w-5 mr-2 text-indigo-600" /> Recent HR
        Notifications
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Pending Confirmations Stack (Hidden if count is 0) */}
        <StackCard
          icon={Briefcase}
          title="Pending Confirmations"
          count={confirmationCount}
          style={STYLES.confirmation}
          onClick={() => handleStackClick("confirmation")}
        />

        {/* Birthdays & Anniversaries Stack (Hidden if count is 0) */}
        <StackCard
          icon={Cake}
          title="Anniversaries & Birthdays"
          count={totalAnniversaryCount}
          style={STYLES.anniversary}
          onClick={() => handleStackClick("anniversary")}
        />

        {/* Future Stack Example (Hidden since count is 0) */}
        <StackCard
          icon={Calendar}
          title="Pending Leave Approvals"
          count={pendingLeaveApprovalCount}
          style={STYLES.leave}
          onClick={() => {
            /* Implement future logic */
          }}
        />
      </div>

      {/* Modals are only rendered/visible when needed */}
      <AnniversaryDetailModal
        isOpen={isAnniversaryModalOpen}
        onClose={() => setIsAnniversaryModalOpen(false)}
        birthdays={birthdayList}
        anniversaries={anniversaryList}
      />

      <ConfirmationDetailModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        confirmations={confirmations}
      />
    </>
  );
};

export default HrDashboardStack;
